/**
 * UsdPricingAdmin — F-173, US-133 Super-Admin Three-Tier Pricing Configuration
 *
 * T-133-001: Ascending tier table (freemium → essentials → professional → enterprise)
 *            with active/inactive badge, baseFee, perUnitRate, includedUnits.
 * T-133-002: Per-tier edit form: baseFee (integer USD) + includedUnits (integer ≥ 1).
 * T-133-003: Overage rate field + per-unit total bill preview calculator +
 *            price-history ledger per tier.
 *
 * Backend: GET  /api/usd-catalog/admin/config
 *          PUT  /api/usd-catalog/admin/config/:tierId
 *          GET  /api/usd-catalog/admin/config/:tierId/history
 *
 * Auth/gating: requires super-admin token (backend enforces; we surface 401/403).
 * No entitlements.js touched — calls shipped routes only.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { landlordApi } from '../../services/api';

// ── Types ──────────────────────────────────────────────────────────────────

interface TierConfig {
  tierId: string;
  name: string;
  baseFee: number;
  perUnitRate: number;
  includedUnits: number;
  usdConversionRate?: number;
  active: boolean;
  configVersion?: number;
}

interface PricingData {
  tiers: Record<string, TierConfig>;
  planOrder: string[];
  editableFields: string[];
}

interface HistoryEntry {
  configVersion: number;
  changedAt: string;
  changedBy: string;
  reason?: string;
  before: Partial<TierConfig>;
  after: Partial<TierConfig>;
}

type Tab = 'table' | 'edit' | 'history';

// ── Bill preview helper (pure, T-133-003) ─────────────────────────────────

function calcBill(baseFee: number, perUnitRate: number, includedUnits: number, unitCount: number): number {
  const overage = Math.max(0, unitCount - includedUnits);
  return baseFee + overage * perUnitRate;
}

// ── Error callout ──────────────────────────────────────────────────────────

function ErrorCallout({ status, message }: { status: number; message: string }) {
  const actions: Record<number, string> = {
    401: 'Your session has expired — please log in again.',
    403: 'Super-admin access required. Contact your system administrator.',
    404: 'Pricing config endpoint not found — check the API base URL.',
    500: 'Server error loading pricing config. Check backend logs.',
  };
  return (
    <div data-testid={`error-callout-${status}`} className="hp-alert hp-alert--error" role="alert">
      <strong>HTTP {status}</strong> — {actions[status] || message}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export function UsdPricingAdmin() {
  const [pricing, setPricing] = useState<PricingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  const [activeTab, setActiveTab] = useState<Tab>('table');
  const [selectedTier, setSelectedTier] = useState<string>('');

  // Edit form state (T-133-002 + T-133-003)
  const [editBaseFee, setEditBaseFee] = useState('');
  const [editIncludedUnits, setEditIncludedUnits] = useState('');
  const [editPerUnitRate, setEditPerUnitRate] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [editReason, setEditReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  // Bill preview (T-133-003)
  const [previewUnits, setPreviewUnits] = useState('1');

  // History (T-133-003)
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [histLoading, setHistLoading] = useState(false);

  const hasSetTier = React.useRef(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setErrorStatus(null);
    try {
      const res = await landlordApi.get('api/usd-catalog/admin/config');
      const d = res?.data?.data ?? res?.data;
      const planOrder: string[] = d?.planOrder ?? [];
      const rawTiers = d?.tiers ?? d ?? {};
      // Normalise: build TierConfig per tier using planOrder
      const tiers: Record<string, TierConfig> = {};
      for (const id of planOrder) {
        const t = rawTiers[id] ?? {};
        tiers[id] = {
          tierId: id,
          name: t.name ?? id,
          baseFee: t.baseFee ?? t.price ?? 0,
          perUnitRate: t.perUnitRate ?? 0,
          includedUnits: t.includedUnits ?? t.maxUnits ?? 1,
          usdConversionRate: t.usdConversionRate,
          active: t.active !== undefined ? t.active : t.price !== undefined,
          configVersion: t.configVersion,
        };
      }
      setPricing({ tiers, planOrder, editableFields: d?.editableFields ?? [] });
      if (planOrder.length && !hasSetTier.current) {
        setSelectedTier(planOrder[0]);
        hasSetTier.current = true;
      }
    } catch (err: any) {
      const s = err?.response?.status ?? 500;
      setErrorStatus(s);
      setErrorMsg(err?.response?.data?.error ?? err?.message ?? 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []); // stable — no deps that change

  useEffect(() => { loadConfig(); }, [loadConfig]);

  // Populate edit form when tier selected
  useEffect(() => {
    if (!pricing || !selectedTier) return;
    const t = pricing.tiers[selectedTier];
    if (!t) return;
    setEditBaseFee(String(t.baseFee));
    setEditIncludedUnits(String(t.includedUnits));
    setEditPerUnitRate(String(t.perUnitRate));
    setEditActive(t.active);
    setEditReason('');
  }, [selectedTier]); // only re-populate when tier selection changes, not on pricing reload

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const body: Record<string, unknown> = {
        baseFee: parseInt(editBaseFee, 10),
        includedUnits: parseInt(editIncludedUnits, 10),
        perUnitRate: parseInt(editPerUnitRate, 10),
        active: editActive,
      };
      if (editReason.trim()) body.reason = editReason.trim();
      const res = await landlordApi.put(`api/usd-catalog/admin/config/${selectedTier}`, body);
      // Update local tier state without triggering a full reload (avoids edit field reset)
      const updated = res?.data?.data;
      if (updated && pricing) {
        setPricing((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            tiers: {
              ...prev.tiers,
              [selectedTier]: {
                ...prev.tiers[selectedTier],
                baseFee: parseInt(editBaseFee, 10),
                includedUnits: parseInt(editIncludedUnits, 10),
                perUnitRate: parseInt(editPerUnitRate, 10),
                active: editActive,
                configVersion: updated.configVersion ?? (prev.tiers[selectedTier]?.configVersion ?? 0) + 1,
              },
            },
          };
        });
      }
      setSaveMsg(`Tier "${selectedTier}" updated successfully.`);
    } catch (err: any) {
      const s = err?.response?.status ?? 500;
      setSaveMsg(`Error ${s}: ${err?.response?.data?.message ?? err?.message ?? 'Save failed'}`);
    } finally {
      setSaving(false);
    }
  };

  const loadHistory = async (tierId: string) => {
    setHistLoading(true);
    try {
      const res = await landlordApi.get(`api/usd-catalog/admin/config/${tierId}/history`);
      const d = res?.data?.data ?? res?.data;
      setHistory(Array.isArray(d) ? d : []);
    } catch {
      setHistory([]);
    } finally {
      setHistLoading(false);
    }
  };

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    if (tab === 'history' && selectedTier) loadHistory(selectedTier);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) return <div data-testid="pricing-loading">Loading pricing configuration…</div>;
  if (errorStatus) return <ErrorCallout status={errorStatus} message={errorMsg} />;
  if (!pricing) return null;

  const planOrder = pricing.planOrder;
  const tiers = pricing.tiers;

  const previewTier = selectedTier ? tiers[selectedTier] : null;
  const previewBill = previewTier
    ? calcBill(
        parseInt(editBaseFee || '0', 10),
        parseInt(editPerUnitRate || '0', 10),
        parseInt(editIncludedUnits || '1', 10),
        parseInt(previewUnits || '1', 10),
      )
    : 0;

  return (
    <div data-testid="usd-pricing-admin" className="hp-dashboard-container">
      <h1>USD Pricing Configuration <span style={{ fontSize: '0.75em', color: '#888' }}>(Super-Admin)</span></h1>

      {/* Tab bar */}
      <div className="hp-tabs" role="tablist">
        {(['table', 'edit', 'history'] as Tab[]).map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={activeTab === t}
            className={`hp-tab${activeTab === t ? ' hp-tab--active' : ''}`}
            onClick={() => handleTabChange(t)}
          >
            {t === 'table' ? 'Tier Table' : t === 'edit' ? 'Edit Tier' : 'Price History'}
          </button>
        ))}
      </div>

      {/* T-133-001: Tier table (ascending order) */}
      {activeTab === 'table' && (
        <div data-testid="tier-table">
          <table className="hp-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th>Tier</th>
                <th>Status</th>
                <th>Base Fee (USD/mo)</th>
                <th>Included Units</th>
                <th>Per-Unit Overage (USD)</th>
                <th>Version</th>
              </tr>
            </thead>
            <tbody>
              {planOrder.map((id) => {
                const t = tiers[id];
                if (!t) return null;
                return (
                  <tr
                    key={id}
                    data-testid={`tier-row-${id}`}
                    onClick={() => { setSelectedTier(id); setActiveTab('edit'); }}
                    style={{ cursor: 'pointer' }}
                  >
                    <td><strong>{t.name}</strong></td>
                    <td>
                      <span
                        data-testid={`tier-status-${id}`}
                        style={{ color: t.active ? 'green' : '#aaa', fontWeight: 600 }}
                      >
                        {t.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td data-testid={`tier-basefee-${id}`}>${t.baseFee}</td>
                    <td data-testid={`tier-included-${id}`}>{t.includedUnits}</td>
                    <td data-testid={`tier-perunit-${id}`}>${t.perUnitRate}</td>
                    <td>{t.configVersion ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p style={{ color: '#666', fontSize: '0.85em', marginTop: 8 }}>
            Click a row to edit that tier.
          </p>
        </div>
      )}

      {/* T-133-002 + T-133-003: Edit form + bill preview */}
      {activeTab === 'edit' && (
        <div data-testid="tier-edit-panel">
          {/* Tier selector */}
          <label htmlFor="tier-select"><strong>Tier:</strong></label>
          <select
            id="tier-select"
            data-testid="tier-select"
            value={selectedTier}
            onChange={(e) => setSelectedTier(e.target.value)}
            style={{ marginLeft: 8, marginBottom: 16 }}
          >
            {planOrder.map((id) => (
              <option key={id} value={id}>{tiers[id]?.name ?? id}</option>
            ))}
          </select>

          {/* T-133-002: Base fee + included units */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <label>
              Base Fee (USD/month)
              <input
                data-testid="input-base-fee"
                type="number"
                min={0}
                value={editBaseFee}
                onChange={(e) => setEditBaseFee(e.target.value)}
                style={{ display: 'block', width: '100%', marginTop: 4 }}
              />
            </label>
            <label>
              Included Units
              <input
                data-testid="input-included-units"
                type="number"
                min={1}
                value={editIncludedUnits}
                onChange={(e) => setEditIncludedUnits(e.target.value)}
                style={{ display: 'block', width: '100%', marginTop: 4 }}
              />
            </label>

            {/* T-133-003: Per-unit overage rate */}
            <label>
              Per-Unit Overage Rate (USD)
              <input
                data-testid="input-per-unit-rate"
                type="number"
                min={0}
                value={editPerUnitRate}
                onChange={(e) => setEditPerUnitRate(e.target.value)}
                style={{ display: 'block', width: '100%', marginTop: 4 }}
              />
            </label>
            <label>
              Active
              <input
                data-testid="input-active"
                type="checkbox"
                checked={editActive}
                onChange={(e) => setEditActive(e.target.checked)}
                style={{ display: 'block', marginTop: 8 }}
              />
            </label>
          </div>

          <label>
            Reason for change (optional)
            <input
              data-testid="input-reason"
              type="text"
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              placeholder="e.g. Q4 repricing"
              style={{ display: 'block', width: '100%', marginTop: 4, marginBottom: 12 }}
            />
          </label>

          <button
            data-testid="btn-save-tier"
            onClick={handleSave}
            disabled={saving}
            style={{ marginRight: 8 }}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>

          {saveMsg && (
            <span
              data-testid="save-message"
              style={{ color: saveMsg.startsWith('Error') ? 'red' : 'green' }}
            >
              {saveMsg}
            </span>
          )}

          {/* T-133-003: Per-unit bill calculator */}
          <div
            data-testid="bill-preview"
            style={{ marginTop: 24, padding: 16, background: '#f5f5f5', borderRadius: 6 }}
          >
            <strong>Bill Preview Calculator</strong>
            <div style={{ marginTop: 8 }}>
              <label>
                Unit count:
                <input
                  data-testid="input-preview-units"
                  type="number"
                  min={1}
                  value={previewUnits}
                  onChange={(e) => setPreviewUnits(e.target.value)}
                  style={{ marginLeft: 8, width: 80 }}
                />
              </label>
              <div data-testid="bill-total" style={{ marginTop: 8, fontSize: '1.2em' }}>
                Estimated monthly bill:{' '}
                <strong>${previewBill}</strong>
                <span style={{ color: '#888', fontSize: '0.8em', marginLeft: 8 }}>
                  (${editBaseFee || 0} base + {Math.max(0, parseInt(previewUnits || '1', 10) - parseInt(editIncludedUnits || '1', 10))} overage × ${editPerUnitRate || 0})
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* T-133-003: Price history ledger */}
      {activeTab === 'history' && (
        <div data-testid="history-panel">
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="history-tier-select"><strong>Tier:</strong></label>
            <select
              id="history-tier-select"
              data-testid="history-tier-select"
              value={selectedTier}
              onChange={(e) => { setSelectedTier(e.target.value); loadHistory(e.target.value); }}
              style={{ marginLeft: 8 }}
            >
              {planOrder.map((id) => (
                <option key={id} value={id}>{tiers[id]?.name ?? id}</option>
              ))}
            </select>
          </div>

          {histLoading && <div data-testid="history-loading">Loading history…</div>}
          {!histLoading && history.length === 0 && (
            <div data-testid="history-empty">No price history recorded for this tier yet.</div>
          )}
          {!histLoading && history.length > 0 && (
            <table className="hp-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Changed At</th>
                  <th>Changed By</th>
                  <th>Reason</th>
                  <th>Before</th>
                  <th>After</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.configVersion} data-testid={`history-row-v${h.configVersion}`}>
                    <td>{h.configVersion}</td>
                    <td>{new Date(h.changedAt).toLocaleString()}</td>
                    <td>{h.changedBy}</td>
                    <td>{h.reason ?? '—'}</td>
                    <td><code>{JSON.stringify(h.before)}</code></td>
                    <td><code>{JSON.stringify(h.after)}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default UsdPricingAdmin;
